import type { InputKey } from '../engine/types';
import { useSound } from '../hooks/useSound';
import { Icon } from './Icon';
import type { TileState } from './Tile';

const ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

interface OnScreenKeyboardProps {
  onKey: (key: InputKey) => void;
  letterStates?: Record<string, TileState | undefined>;
  disabled?: boolean;
  /** Letter-only mode: hide Enter / Delete (e.g. Petal's letter-by-letter play). */
  compact?: boolean;
  /** Per-letter disabled flags (e.g. already-guessed letters in Petal). */
  disabledKeys?: Record<string, boolean>;
}

/** QWERTY input for letter-guessing modes; keys tint to reflect clue state. */
export function OnScreenKeyboard({
  onKey,
  letterStates = {},
  disabled,
  compact,
  disabledKeys = {},
}: OnScreenKeyboardProps) {
  const sound = useSound();

  const press = (key: InputKey, name: 'key' | 'submit' | 'delete') => {
    if (disabled) return;
    sound.play(name);
    onKey(key);
  };

  return (
    <div className="cv-keyboard" role="group" aria-label="On-screen keyboard">
      {ROWS.map((row, ri) => (
        <div className="cv-keyboard__row" key={row}>
          {ri === 2 && !compact && (
            <button
              type="button"
              className="cv-key cv-key--wide cv-key--accent"
              onClick={() => press({ type: 'enter' }, 'submit')}
              disabled={disabled}
            >
              Enter
            </button>
          )}
          {row.split('').map((ch) => (
            <button
              key={ch}
              type="button"
              className="cv-key"
              data-state={letterStates[ch]}
              onClick={() => press({ type: 'char', value: ch }, 'key')}
              disabled={disabled || disabledKeys[ch]}
              aria-label={`Letter ${ch}`}
            >
              {ch}
            </button>
          ))}
          {ri === 2 && !compact && (
            <button
              type="button"
              className="cv-key cv-key--wide"
              onClick={() => press({ type: 'backspace' }, 'delete')}
              disabled={disabled}
              aria-label="Delete"
            >
              <Icon name="back" size={18} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
